import { useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { Box, Button, Typography } from '@mui/material';

import { wf } from './tokens';

interface IntakeStaffVerifyBarProps {
  verifiedAt?: string | null;
  onVerifyIntake?: () => void | Promise<void>;
  onDiscardVerification?: () => void | Promise<void>;
  isVerifyingIntake?: boolean;
  isDiscardingVerification?: boolean;
  /** Present while verifying is not possible yet; shown beside the button. */
  verifyBlockedReason?: string;
}

const VERIFY_EFFECTS =
  'Confirming marks this provider verified for the organization, feeds the organization progress and roster status, and delivers the intake verification event to downstream systems.';
const DISCARD_EFFECTS =
  'Discarding removes the verification marker, returns this provider to the submitted state on the organization roster and progress, and allows the intake to be verified again later. Systems already notified of the verification are not notified of the discard.';

/**
 * The staff verification control, pinned to the intake shell so it stays in
 * reach on every intake page rather than only the completion page. Mount it
 * once — the shell's action bar slot is its only host.
 */
export function IntakeStaffVerifyBar({
  verifiedAt = null,
  onVerifyIntake,
  onDiscardVerification,
  isVerifyingIntake = false,
  isDiscardingVerification = false,
  verifyBlockedReason,
}: IntakeStaffVerifyBarProps) {
  const [pendingAction, setPendingAction] = useState<'verify' | 'discard' | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const isVerified = Boolean(verifiedAt);

  // The dialog stays open until the mutation settles: closing on click would
  // unmount the loading state and re-enable both buttons mid-flight, which
  // invites a second submission of the same action.
  const confirmAction = async (action?: () => void | Promise<void>) => {
    if (isConfirming) return;
    setIsConfirming(true);
    try {
      await action?.();
    } finally {
      setIsConfirming(false);
      setPendingAction(null);
    }
  };

  return (
    <Box
      aria-label="Intake verification"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '10px',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: wf.ink, fontSize: '13px', fontWeight: 700 }}>
          {isVerified ? 'This intake is verified' : 'Admin review'}
        </Typography>
        <Typography sx={{ color: wf.muted, fontSize: '12.5px', lineHeight: 1.4 }}>
          {isVerified
            ? 'This provider is marked verified for the organization.'
            : (verifyBlockedReason ??
              'All documents and fields have been submitted and are ready for Admin review.')}
        </Typography>
      </Box>
      {isVerified ? (
        <Button
          variant="outlined"
          color="warning"
          size="small"
          disabled={isDiscardingVerification || isConfirming || !onDiscardVerification}
          onClick={() => setPendingAction('discard')}
          sx={{ flexShrink: 0, fontWeight: 700 }}
        >
          {isDiscardingVerification ? 'Discarding…' : 'Discard verification'}
        </Button>
      ) : (
        <Button
          variant="contained"
          size="small"
          disabled={
            isVerifyingIntake || isConfirming || Boolean(verifyBlockedReason) || !onVerifyIntake
          }
          onClick={() => setPendingAction('verify')}
          title={verifyBlockedReason}
          sx={{ flexShrink: 0, fontWeight: 700 }}
        >
          {isVerifyingIntake ? 'Verifying…' : 'This all looks great — verify intake'}
        </Button>
      )}

      <ConfirmDialog
        open={pendingAction === 'verify'}
        variant="info"
        title="Verify this intake?"
        message={VERIFY_EFFECTS}
        confirmText="Verify intake"
        loading={isVerifyingIntake || isConfirming}
        onClose={() => {
          if (isConfirming) return;
          setPendingAction(null);
        }}
        onConfirm={() => void confirmAction(onVerifyIntake)}
      />
      <ConfirmDialog
        open={pendingAction === 'discard'}
        variant="warning"
        title="Discard this verification?"
        message={DISCARD_EFFECTS}
        confirmText="Discard verification"
        loading={isDiscardingVerification || isConfirming}
        onClose={() => {
          if (isConfirming) return;
          setPendingAction(null);
        }}
        onConfirm={() => void confirmAction(onDiscardVerification)}
      />
    </Box>
  );
}
